import PropTypes from 'prop-types';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import JsonNode from './JsonTree/JsonNode';

const getPanelStyles = (variant) => {
  if (variant === 'hud') {
    return {
      width: '100%',
      minWidth: 140,
      maxHeight: 'calc(72vh - 80px)',
      border: '1px solid rgba(0,255,170,0.2)',
      background: 'linear-gradient(160deg, rgba(6,21,29,0.92) 0%, rgba(10,32,40,0.9) 50%, rgba(12,40,46,0.88) 100%)',
      color: '#e8fdfc'
    };
  }
  return {
    width: '100%',
    minWidth: 280,
    border: (theme) => `1px solid ${theme.palette.divider}`,
    backgroundColor: (theme) => theme.palette.background.paper,
    color: 'inherit'
  };
};

const PinnedList = ({
  entries = [],
  onUnpin,
  onEdit,
  onTogglePin,
  pinnedPathSet,
  variant = 'page',
  sx = {},
  onAddProperty,
  activeInlineAddPath,
  onInlineAddPathChange,
  priorityMap = {},
  exactMatchOnly = false
}) => {
  const styles = getPanelStyles(variant);
  const appearance = variant === 'hud' ? 'hud' : 'page';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', borderRadius: 1, padding: 1.25, gap: 1, overflow: 'hidden', ...styles, ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
          Pinned
        </Typography>
        <PushPinIcon fontSize="small" sx={{ opacity: 0.7 }} />
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: 4 } }}>
        {entries.length === 0 && (
          <Box
            sx={{
              borderRadius: 1,
              border: variant === 'hud' ? '1px dashed rgba(0,255,170,0.25)' : '1px dashed rgba(0,0,0,0.15)',
              backgroundColor: variant === 'hud' ? 'rgba(9, 30, 38, 0.65)' : 'rgba(0,0,0,0.025)',
              py: 1.5,
              px: 1.25,
              textAlign: 'center'
            }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: variant === 'hud' ? '#bfece5' : 'text.secondary' }}>
              Pin frequently inspected nodes to keep them here.
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: variant === 'hud' ? 'rgba(255,255,255,0.65)' : 'text.secondary' }}>
              Use the check icon next to any row to pin it.
            </Typography>
          </Box>
        )}
        {entries.map((entry) => (
          <Box
            key={entry.pathKey}
            sx={{
              borderRadius: 1,
              border: variant === 'hud' ? '1px solid rgba(0,255,170,0.25)' : '1px solid rgba(0,0,0,0.12)',
              backgroundColor: variant === 'hud' ? 'rgba(9, 30, 38, 0.78)' : 'rgba(0,0,0,0.02)',
              padding: 0.75,
              mb: 1
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {entry.pathKey}
              </Typography>
              <IconButton size="small" onClick={() => onUnpin?.(entry.pathKey)} sx={{ color: appearance === 'hud' ? '#00ffaa' : 'inherit' }}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Box>
            <JsonNode
              label={entry.label || entry.pathKey.split('.').pop() || entry.pathKey}
              value={entry.value}
              path={entry.pathKey}
              keyPath={entry.keyPath}
              level={0}
              defaultOpen={false}
              searchTerm=""
              onEdit={onEdit}
              appearance={appearance}
              supportsTouch
              showAllDescendants
              onTogglePin={onTogglePin}
              pinnedPathSet={pinnedPathSet}
              onAddProperty={onAddProperty}
              activeInlineAddPath={activeInlineAddPath}
              onInlineAddPathChange={onInlineAddPathChange}
              priorityMap={priorityMap}
              exactMatchOnly={exactMatchOnly}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

PinnedList.propTypes = {
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      pathKey: PropTypes.string.isRequired,
      keyPath: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  onUnpin: PropTypes.func,
  onEdit: PropTypes.func,
  onTogglePin: PropTypes.func,
  pinnedPathSet: PropTypes.instanceOf(Set),
  variant: PropTypes.oneOf(['page', 'hud']),
  sx: PropTypes.object,
  onAddProperty: PropTypes.func,
  activeInlineAddPath: PropTypes.string,
  onInlineAddPathChange: PropTypes.func,
  priorityMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  exactMatchOnly: PropTypes.bool
};

export default PinnedList;
